// ... existing imports
import React, { useState, useRef, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import "./Homepage.css";
import SoundConceptFrame from "./frame.js";
import ConceptFrame from "./frame1.js";
import MiddleFrame from "./frame2.js";
import vector187 from "./vector-187.png";
import group283 from "./group-283.png";
import Cloud from "./Cloud";
import Title from "./title.js";
import SoundToggle from "./SoundToggle";
import { Footer } from "./Footer";
import audioService from "./AudioService";
import { fetchSoundData } from "./Data/parseSheetData"
import blackSectionBottomShape from "./black-section-bottom-shape.svg";
import blackSectionTopShape from "./black-section-top-shape.svg";

const useLazySection = (rootMargin) => {
  const ref = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isReady) return undefined;

    const element = ref.current;
    if (!element) return undefined;

    if (!("IntersectionObserver" in window)) {
      const timerId = setTimeout(() => setIsReady(true), 300);
      return () => clearTimeout(timerId);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsReady(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isReady, rootMargin]);

  return [ref, isReady];
};

const FramePlaceholder = () => <div className="home-frame-placeholder" aria-hidden="true" />;

export default function Homepage({ audioControls, poemImageUrl, TitleImage }) {
  const [showTrapeziumText, setShowTrapeziumText] = useState(false);
  const [showPartneringText, setShowPartneringText] = useState(false);
  const [showSoundConceptText, setShowSoundConceptText] = useState(false);
  const [isFrameHovered, setIsFrameHovered] = useState(false);
  const [isInTrapezium, setIsInTrapezium] = useState(false);
  // const [audioEnabled, setAudioEnabled] = useState(true);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const interactive = useRef(null);
  const readMoreBtnRef = useRef(null);
  const conceptBtnRef = useRef(null);
  const conceptParaRef = useRef(null);
  const soundBtnRef = useRef(null);
  const [sheetData, setSheetData] = useState([]);
  const isMobile = useMediaQuery({ maxWidth: 820 });
  const [conceptSectionRef, showConceptFrame] = useLazySection("2000px");
  const [trapeziumSectionRef, showTrapeziumFrame] = useLazySection("2000px");
  const [soundSectionRef, showSoundFrame] = useLazySection("2000px");
  // const shouldRenderClouds = !isMobile;

  const handleConceptToggle = () => {
    const nextState = !showPartneringText;
    setShowPartneringText(nextState);
    if (nextState) {
      if (conceptBtnRef.current) {
        conceptBtnRef.current.blur();
      }
      setTimeout(() => {
        if (conceptBtnRef.current) {
          conceptBtnRef.current.blur();
        }
      }, 0);
    } else {
      setTimeout(() => {
        if (conceptBtnRef.current) {
          if (conceptParaRef.current) {
            conceptParaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          conceptBtnRef.current.focus({ preventScroll: true });
        }
      }, 100);
    }
  };

  const handleSoundConceptToggle = () => {
    const nextState = !showSoundConceptText;
    setShowSoundConceptText(nextState);
    if (nextState) {
      if (soundBtnRef.current) {
        soundBtnRef.current.blur();
      }
      setTimeout(() => {
        if (soundBtnRef.current) {
          soundBtnRef.current.blur();
        }
      }, 0);
    } else {
      setTimeout(() => {
        if (soundBtnRef.current) {
          soundBtnRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          soundBtnRef.current.focus({ preventScroll: true });
        }
      }, 100);
    }
  };

  const handleTrapeziumToggle = () => {
    const nextState = !showTrapeziumText;
    setShowTrapeziumText(nextState);
    if (nextState) {
      if (readMoreBtnRef.current) {
        readMoreBtnRef.current.blur();
      }
      setTimeout(() => {
        if (readMoreBtnRef.current) {
          readMoreBtnRef.current.blur();
        }
      }, 0);
    } else {
      setTimeout(() => {
        if (readMoreBtnRef.current) {
          readMoreBtnRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          readMoreBtnRef.current.focus({ preventScroll: true });
        }
      }, 100);
    }
  };

  useEffect(() => {
    return undefined;
  }, [showConceptFrame, showTrapeziumFrame]);

  useEffect(() => {
    const loadSheetData = async () => {
      try {
        const rows = await fetchSoundData();
        setSheetData(rows);
      } catch (err) {
        console.error("Failed to load sound data:", err);
      }
    };

    loadSheetData();
  }, []);
  const formatContent = (text) => {
    if (!text) return "";

    // Convert markdown links to anchor tags
    let formatted = text.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      `<a href="$2" target="_blank" rel="noopener noreferrer" class="dynamicLink">$1</a>`
    );

    // Convert line breaks
    formatted = formatted.replace(/\n/g, "<br />");

    return formatted;
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
    if (interactive.current) {
      interactive.current.scrollLeft = interactive.current.scrollWidth;
    }
  }, []);

  useEffect(() => {
    const trapeziumObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]) {
          setIsInTrapezium(entries[0].isIntersecting);
        }
      },
      { threshold: 0.3 }
    );

    const trapezium = document.querySelector(".trapezium-section");
    if (trapezium) {
      trapeziumObserver.observe(trapezium);
    }

    return () => trapeziumObserver.disconnect();
  }, []);

  const handleFrameHover = (hovering) => {
    setIsFrameHovered(hovering);
  };

  const handleAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };
  useEffect(() => {
    const startAudio = async () => {
      audioService.setLoadingScreen(false);

      await audioService.playAmbientSound(1.0, 1500);
    };

    startAudio();
  }, []);


  return (
    <div className="homepage">
      <div className="homepage-content">
        <SoundToggle
          padNumber={999}
          isInTrapezium={isInTrapezium}
          panelMode={isInTrapezium ? "black" : "white"}
          defaultActive={true}
        />

        {/* Header */}
        <section className="header">
          <div
            className="header-container"
            style={{
              width: window.innerWidth <= 768 ? "85%" : "auto",
              height: window.innerWidth <= 768 ? "10%" : "auto",
            }}
          >
            <Title poemImageUrl={poemImageUrl} TitleImage={TitleImage} />
          </div>
        </section>

        {/* Concept Section */}
        <section ref={conceptSectionRef} className="concept-section" style={{ overflow: "visible" }}>
          {!isMobile && (
            <>
              <Cloud top={-60} left={45} distance="short" direction="left" variant={1} />
            </>
          )}
          <Cloud top={isMobile ? 308 : 10} left={isMobile ? 100 : 60} distance="medium" direction="left" variant={2} />
          <Cloud top={isMobile ? 135 : -170} left={isMobile ? 100 : 63} distance="long" direction="left" variant={3} />
          {/* {shouldRenderClouds && (
            <>
            </>
          )} */}

          <div className="concept-text">
            <h2>{sheetData[0]?.Title_1}</h2>
            <p ref={conceptParaRef}
              dangerouslySetInnerHTML={{
                __html: formatContent(sheetData[0]?.Para_1),
              }}
            />
            {showPartneringText && (
              <p
                dangerouslySetInnerHTML={{
                  __html: formatContent(sheetData[0]?.Para_2),
                }}
              />
            )}

            <div style={{ justifyContent: "flex-start", display: isMobile ? "block" : "flex", }}>
              <div
                ref={conceptBtnRef}
                tabIndex={0}
                role="button"
                className="read-more-box"
                onClick={handleConceptToggle}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleConceptToggle();
                  }
                }}
              >
                {showPartneringText ? "READ LESS" : "READ MORE"}
              </div>
            </div>
          </div>

          <div className="svg-container-concept" ref={interactive}>
            {showConceptFrame ? (
              <ConceptFrame
                className="interactive-svg-concept"
                preserveAspectRatio="xMidYMid meet"
                audioRef={audioRef}
                handleAudio={handleAudio}
                onHover={handleFrameHover}
                audioControls={audioControls}
              />
            ) : (
              <FramePlaceholder />
            )}
          </div>

          <div className="arrow-box">
            <img className="group" alt="Group" src={group283} />
          </div>
        </section>

        <section
          ref={trapeziumSectionRef}
          className={`trapezium-section ${isMobile && showTrapeziumText ? "trapezium-section-expanded" : ""
            }`}
          style={{ overflow: "visible" }}
        >
          <img
            src={isMobile ? blackSectionBottomShape : blackSectionTopShape}
            className="black-section-top-shape"
            alt=""
          />
          <div className="svg-container-trapezium" ref={containerRef}>
            {showTrapeziumFrame ? (
              <MiddleFrame
                className="middle-svg-concept"
                preserveAspectRatio="xMidYMid meet"
                audioRef={audioRef}
                handleAudio={handleAudio}
                audioControls={audioControls}
              />
            ) : (
              <FramePlaceholder />
            )}
          </div>
          <div className="trapezium-text">
            <h2>{(sheetData[0]?.Title_2)}</h2>
            <p
              dangerouslySetInnerHTML={{
                __html: formatContent(sheetData[0]?.Para_3),
              }}
            />

            {showTrapeziumText && (
              <p
                dangerouslySetInnerHTML={{
                  __html: formatContent(sheetData[0]?.Para_4),
                }}
              />
            )}

            <div style={{ display: "flex", justifyContent: isMobile ? "center" : "flex-start", width: "100%", marginTop: "10px" }}>
              <div
                ref={readMoreBtnRef}
                tabIndex={0}
                role="button"
                className="read-more-box"
                onClick={handleTrapeziumToggle}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleTrapeziumToggle();
                  }
                }}
              >
                {showTrapeziumText ? "READ LESS" : "READ MORE"}
              </div>
            </div>
          </div>
          <img
            src={isMobile ? blackSectionTopShape : blackSectionBottomShape}
            className="black-section-bottom-shape"
            alt=""
          />
        </section>


        {/* Sound Concept Section */}
        <section ref={soundSectionRef} className="sound-concept-section" style={{ overflow: "visible" }}>
          <Cloud
            top={isMobile ? -123 : -183}
            left={56}
            distance="short"
            direction="left"
            variant={1}
            zIndex={6}
          />

          <Cloud
            top={-20}
            left={54}
            distance="medium"
            direction="left"
            variant={2}
            zIndex={5}
          />
          {/* {shouldRenderClouds && (
            <>
            </>
          )} */}
          <div className="sound-text">
            <h2>{sheetData[0]?.Title_3}</h2>
            <p
              dangerouslySetInnerHTML={{
                __html: formatContent(sheetData[0]?.Para_5),
              }}
            />
            {showSoundConceptText && (
              <p
                dangerouslySetInnerHTML={{
                  __html: formatContent(sheetData[0]?.Para_6),
                }}
              />
            )}

            <div style={{ justifyContent: "flex-start", display: isMobile ? "block" : "flex", }}>
              <div
                ref={soundBtnRef}
                tabIndex={0}
                role="button"
                className="read-more-box"
                onClick={handleSoundConceptToggle}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSoundConceptToggle();
                  }
                }}
              >
                {showSoundConceptText ? "READ LESS" : "READ MORE"}
              </div>
            </div>
          </div>

          <div className="svg-container-scs">
            {showSoundFrame ? (
              <SoundConceptFrame
                className="strategy-image interactive-svg"
                preserveAspectRatio="xMidYMid meet"
                audioRef={audioRef}
                handleAudio={handleAudio}
                audioControls={audioControls}
              />
            ) : (
              <FramePlaceholder />
            )}
          </div>
        </section>

        <div className="vertical-line-section">
          <div
            style={{
              marginTop: "50px",
              width: "1px",
              height: "150px",
              backgroundColor: "#000",
              marginLeft: window.innerWidth <= 768 ? "50%" : "50px"
            }}
          />
        </div>

        <footer className="footer-section mt-0">
          <div className="box">
            <img className="vector" alt="Vector" src={vector187} />
          </div>
          <Footer />
        </footer>
      </div>
    </div>
  );
}
